import React, {
  ReactElement,
  useEffect,
  useMemo,
  useState,
  useContext
} from 'react';
import {
  Typography,
  Button,
  ArrowNextIcon,
  ArrowDownIcon
} from '@customink/pigment-react';
import moment from 'moment';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Table from '@mui/material/Table';
import Box from '@mui/material/Box';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useGetPurchasesQuery} from '../../api/storesApi';
import {useAppSelector, useCampaignStatus, useStoreType} from '../../hooks';
import services from '../../api/remoteServices';
import DashboardToolTip from './DashboardToolTip';
import PurchaseItemsCsvExportButton from './PurchaseItemsCsvExportButton';
import ReplacementCampaignButtons from './ReplacementCampaignButtons';
import {useFetchUserQuery} from '../../api/userApi';
import {selectAdmin} from '../../slices/userSlice';
import {PurchaseCollection} from '../../models/storePurchase';
import {getComparator, stableSort} from '../../utils/tableHelpers';
import OrderActionDropdown from './OrderActionDropdown';
import {OrderListingPlaceHolder} from './DashboardPlaceHolder';
import {MobileContext} from '../../containers/MobileProvider';
import {formatCurrency} from '../../utils/helpers';

type DashboardOrderListingProps = {
  storeSlug: string;
};
type OrderSummaryKey = keyof PurchaseCollection;

const RIGHT_ALIGNED_KEYS = ['totalActiveQuantity', 'currentTotal'];
const ORDER_SUMMARY_ITEMS = {
  subtotal: 'Subtotal',
  shippingCost: 'Shipping',
  tax: 'Taxes',
  currentTotal: 'Total'
};
const HEADER_LABEL = {
  createdAt: 'Purchase Date',
  email: 'Name and Email',
  purchaseItemsSummary: 'Item',
  totalActiveQuantity: 'Quantity',
  currentTotal: 'Total'
};

const HEADER_INFO_TOOLTIP = {
  currentTotal: (
    <DashboardToolTip>
      <Typography>
        For orders that are shipped directly to your group members, your total
        will include a group member shipping charge. If a group member is paying
        for the order, their total will include a per-item transaction fee.
      </Typography>
    </DashboardToolTip>
  )
};
const bodyInfoToolTip = (order?: PurchaseCollection) => {
  return (
    <DashboardToolTip position="top-start">
      <ul className="price-tooltip-breakouts-ul">
        {Object.keys(ORDER_SUMMARY_ITEMS).map((key: OrderSummaryKey, index) => {
          const value = Number(order[key]);
          return (
            <li
              key={key}
              className={`price-tooltip-breakouts-li${
                index === Object.keys(ORDER_SUMMARY_ITEMS).length - 1
                  ? '-last'
                  : ''
              }`}>
              {ORDER_SUMMARY_ITEMS[key as keyof typeof ORDER_SUMMARY_ITEMS]}:{' '}
              {formatCurrency(value)}
            </li>
          );
        })}
      </ul>
    </DashboardToolTip>
  );
};
const getDate = (createdAt: string, large: boolean) => {
  if (!createdAt) return '';

  const orderedDate = moment(createdAt);
  const orderDay = orderedDate.format('MM/DD/YY');
  const orderTime = orderedDate.format('hh:mm a');
  return (
    <Typography variant="captionLarge">
      {orderDay}&nbsp;{large && <br />}
      <span className="timeAt">at {orderTime}</span>
    </Typography>
  );
};
const getEmailandName = (
  email: string,
  firstName: string,
  lastName: string
) => {
  return (
    <Typography variant="captionLarge">
      {`${firstName} ${lastName}`}
      <br />
      <a href={`mailto:${email}`}>{email}</a>
    </Typography>
  );
};
const getItemSummary = (
  summary: PurchaseCollection['purchaseItemsSummary']
) => {
  if (!summary) return '';
  return (
    <table className="orderSummaryContainer">
      <tbody>
        {summary.map((item, index) => {
          return (
            <tr
              key={item.productName + index.toString()}
              className={`row${index !== summary.length - 1 ? '-top' : ''}`}>
              <td colSpan={3}>
                <Typography variant="captionLarge">
                  {item.color && item.size
                    ? `${item.productName} (${item.color}, ${item.size})`
                    : item.productName}
                </Typography>
              </td>
              <td className="itemQuantity">
                <Typography variant="captionLarge">{item.quantity}</Typography>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const DashboardOrderListing: React.FC<DashboardOrderListingProps> = ({
  storeSlug
}: DashboardOrderListingProps): ReactElement => {
  const {isStoreTypeGroupOrder} = useStoreType();
  const storeUrl = isStoreTypeGroupOrder
    ? `${services.storefront}/${storeSlug}`
    : `${services.stores}/${storeSlug}`;
  const {
    data,
    isLoading: isPurchaseDataLoading,
    isSuccess: isPurchaseSuccess
  } = useGetPurchasesQuery(storeSlug);
  useFetchUserQuery();

  const {isCampaignCompleted} = useCampaignStatus();
  const isAdmin = useAppSelector(selectAdmin);

  const [header, setHeader] = useState([]);
  const [rows, setRows] = useState<PurchaseCollection[]>([]);
  const order = 'desc'; // will be modified when sorting feature is added later
  const orderBy = 'createdAt'; // will be modified when sorting feature is added later
  const theme = useTheme();
  const largeBreakPoint = useMediaQuery(theme.breakpoints.down('lg'));
  const mobile = useContext(MobileContext);

  useEffect(() => {
    if (!isPurchaseSuccess || !data.purchases?.length) {
      setHeader([]);
      setRows([]);
      return;
    }

    const headerData = Object.keys(HEADER_LABEL).reduce((acc, key: string) => {
      const obj = {
        id: key,
        label: HEADER_LABEL[key as keyof typeof HEADER_LABEL],
        disablePadding: false,
        renderIcon: () =>
          HEADER_INFO_TOOLTIP[key as keyof typeof HEADER_INFO_TOOLTIP]
      };
      acc.push(obj);
      return acc;
    }, []);

    setHeader(headerData);
    setRows(data.purchases);
  }, [isPurchaseSuccess, data]);

  const NoOrderMessage = () => {
    return (
      <div className="dashboard-orderListing-noOrder">
        <Typography className="message-header" variant="bodyShort2">
          No Orders Yet
        </Typography>
        <Typography className="message-body" variant="bodyShort1" gutterBottom>
          You’ll be able to see the details of all of your orders here.
        </Typography>
        <a
          href={storeUrl}
          target="_blank"
          rel="noreferrer"
          className="addOrderLink">
          <Button className="add-an-order-button" variant="secondary">
            Add An Order
          </Button>
        </a>
      </div>
    );
  };
  const OrderTableHeader = () => {
    return (
      <TableHead className="table-header">
        <TableRow>
          <TableCell className="headCell-first" />
          {header?.map(headCell => (
            <TableCell
              key={headCell.id}
              className={`${headCell.id}`}
              align={
                RIGHT_ALIGNED_KEYS.includes(headCell.id) ? 'right' : 'left'
              }>
              <div className={`${headCell.label.toLowerCase()}-wrapper`}>
                {headCell.label}
                {headCell.renderIcon()}
              </div>
            </TableCell>
          ))}
          <TableCell align="right" className="headCell-last">
            Actions
          </TableCell>
        </TableRow>
      </TableHead>
    );
  };

  const OrderTableRow = (props: {
    index: number;
    row: PurchaseCollection;
    large: boolean;
  }) => {
    const {index, row, large} = props;
    const [open, setOpen] = useState(false);

    const orderRow = () => {
      return (
        <TableRow className="order-row">
          <TableCell className="expansion">
            <IconButton
              className="arrowIconButton"
              aria-label={open ? 'Collapse row' : 'Expand row'}
              size="small"
              onClick={() => setOpen(!open)}>
              {open ? (
                <span>
                  <ArrowDownIcon className="arrowIcon" />
                </span>
              ) : (
                <span>
                  <ArrowNextIcon className="arrowIcon" />
                </span>
              )}
            </IconButton>
          </TableCell>
          <TableCell className="purchaseDate">
            {getDate(row.createdAt, large)}
          </TableCell>
          <TableCell className="email">
            {getEmailandName(row.email, row.firstName, row.lastName)}
          </TableCell>
          <TableCell className="itemSummary" colSpan={2}>
            {getItemSummary(row.purchaseItemsSummary)}
          </TableCell>
          <TableCell align="right" className="total">
            <div className="total-wrapper">
              <Typography variant="captionLarge">
                {formatCurrency(Number(row.currentTotal))}
              </Typography>
              {bodyInfoToolTip(row)}
            </div>
          </TableCell>
          <TableCell className="orderAction">
            <OrderActionDropdown
              storeSlug={storeSlug}
              referenceId={row.referenceId}
              status={row.status}
            />
          </TableCell>
        </TableRow>
      );
    };
    const shippingRow = () => {
      return (
        <TableRow
          className={`shipping-row${index === rows.length - 1 ? '-last' : ''}`}>
          <TableCell className="shipping-row-cell" colSpan={7}>
            <Collapse in={open} timeout={1000} unmountOnExit>
              <Box className="shipping-row-cell-box">
                <Table size="small" aria-label="purchases">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Ship to</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <Typography variant="captionLarge">
                          {row.referenceId}
                        </Typography>
                      </TableCell>
                      <TableCell className="status">
                        <Typography variant="captionLarge">
                          {row.status}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="captionLarge">
                          {row.shippingToLocation
                            ? 'Organizer'
                            : 'Group Member'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      );
    };
    return (
      <>
        {orderRow()}
        {shippingRow()}
      </>
    );
  };

  const renderTableHeader = useMemo(() => {
    if (!header.length) return '';

    return <OrderTableHeader />;
  }, [header]);

  const renderOrderListingTable = useMemo(() => {
    if (!rows.length) return <NoOrderMessage />;
    return (
      <Table className="orderListingTable">
        {renderTableHeader}
        <TableBody>
          {data?.purchases.length &&
            stableSort<PurchaseCollection>(
              rows,
              getComparator(order, orderBy)
            ).map((row: PurchaseCollection, index: number) => (
              <OrderTableRow
                index={index}
                // eslint-disable-next-line react/prop-types
                key={row.referenceId}
                row={row}
                large={largeBreakPoint}
              />
            ))}
        </TableBody>
      </Table>
    );
  }, [rows, largeBreakPoint]);

  const orderListingClasses = `dashboard-orderListing${
    mobile ? ' mobile' : ''
  }`;
  return (
    <div className={orderListingClasses}>
      <div className="dashboard-orderListing-header">
        <span>
          <Typography className="section-title" variant="subHeading">
            Orders
          </Typography>
        </span>
        <span className="orderListingHeaderButtons">
          <PurchaseItemsCsvExportButton storeSlug={storeSlug} />
          {isAdmin && isCampaignCompleted ? (
            <ReplacementCampaignButtons storeSlug={storeSlug} />
          ) : null}
        </span>
      </div>
      {isPurchaseDataLoading ? (
        <OrderListingPlaceHolder />
      ) : (
        <div className="dashboard-orderListing-table">
          {renderOrderListingTable}
        </div>
      )}
    </div>
  );
};

export default DashboardOrderListing;
